import { useEffect, useRef, useState } from "react";
import { generateClient } from "aws-amplify/api";
import type { ArrivalCardData } from "../components/abdm/ScanArrivalCard";
import { logger } from "../utils/logger";

const SCAN_SUBSCRIPTION = /* GraphQL */ `
  subscription OnNotification {
    onNotification {
      notificationId
      clinicId
      eventType
      payload
      createdAt
    }
  }
`;

function safeJson(s: string): Record<string, unknown> {
  try {
    const p = JSON.parse(s);
    return typeof p === "object" && p !== null ? p : {};
  } catch {
    return {};
  }
}

function calcAge(dob: string): number | undefined {
  try {
    const birth = new Date(dob);
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    if (
      now.getMonth() < birth.getMonth() ||
      (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
    )
      age--;
    return age >= 0 && age < 150 ? age : undefined;
  } catch {
    return undefined;
  }
}

export function useAbdmScanCards() {
  const [cards, setCards] = useState<ArrivalCardData[]>([]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let sub: { unsubscribe: () => void } | null = null;
    try {
      const client = generateClient();
      const result = client.graphql({ query: SCAN_SUBSCRIPTION });
      if (result && typeof (result as any).subscribe === "function") {
        sub = (result as any).subscribe({
          next: (event: any) => {
            const incoming = event?.data?.onNotification;
            if (!incoming || !mounted.current) return;
            if (incoming.eventType !== "patient_scan_received") return;

            const p =
              typeof incoming.payload === "string"
                ? safeJson(incoming.payload)
                : (incoming.payload ?? {});
            const demo = (p.demographics as Record<string, unknown>) ?? {};

            setCards((prev) => [
              ...prev,
              {
                patient_name: String(p.patient_name ?? "Unknown"),
                abha_id: String(p.abha_id ?? ""),
                age: demo.dob ? calcAge(String(demo.dob)) : undefined,
                gender: demo.gender ? String(demo.gender) : undefined,
                matched: !p.is_new_patient,
                patient_id: p.matched_patient_id ? String(p.matched_patient_id) : null,
                last_visit_at: p.last_visit_at ? String(p.last_visit_at) : undefined,
                demographics: {
                  name: p.patient_name ? String(p.patient_name) : undefined,
                  dob: demo.dob ? String(demo.dob) : null,
                  gender: demo.gender ? String(demo.gender) : null,
                },
              },
            ]);
          },
          error: (err: unknown) => {
            logger.warn("ABDM scan subscription error", {
              error: err instanceof Error ? err.message : String(err),
            });
          },
        });
      }
    } catch (err) {
      logger.warn("ABDM scan subscription unavailable", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
    return () => { sub?.unsubscribe(); };
  }, []);

  const dismiss = (idx: number) =>
    setCards((c) => c.filter((_, i) => i !== idx));

  return { cards, dismiss };
}
